import { AssignmentTurnedIn } from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from "@mui/icons-material/Edit";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Badge,
  Box,
  Button,
  Dialog,
  Menu,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { PageHeader } from "common";
import FormEditorMenu from "components/FormEditor/FormEditorMenu";
import { SharingListItem } from "features/sharings/sharingApi";
import SharingForm from "features/sharings/SharingForm";
import useSharingList from "features/sharings/useSharingList";
import I18n from "i18n-js";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { WaiverStatus } from "./waiverApi";
import WaiverProvider, { useWaiverState } from "./WaiverProvider";

const TabContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { waiver_id } = useParams();
  const waiverContext = useWaiverState();
  const {
    item,
    loading,
    handleStatusChange,
    sharingOpen,
    setSharingOpen,
    sharingDialog,
    isDirty,
  } = waiverContext;

  const getTabValue = () => {
    const path = location.pathname;
    if (path.endsWith("/edit")) return 1;
    if (path.includes("/publish")) return 2;
    if (path.endsWith("/submits")) return 3;
    return 0; // preview tab
  };

  const [tabValue, setTabValue] = useState(getTabValue());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const sharingList = useSharingList({ waiver_id });
  const sharings = sharingList.rows || [];

  useEffect(() => {
    setTabValue(getTabValue());
  }, [location.pathname]);

  const handlePreviewClick = (event: React.MouseEvent<HTMLElement>) => {
    if (sharings.length === 1) {
      const url = `${window.location.origin}/sharing/${sharings[0].token}`;
      window.open(url, "_blank");
    } else if (sharings.length > 1) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSharingSelect = (sharing: SharingListItem) => {
    const url = `${window.location.origin}/sharing/${sharing.token}`;
    window.open(url, "_blank");
    handleMenuClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const basePath = `/waivers/${waiver_id}`;

    switch (newValue) {
      case 0:
        navigate(basePath);
        break;
      case 1:
        navigate(`${basePath}/edit`);
        break;
      case 2:
        navigate(`${basePath}/publish`);
        break;
      case 3:
        navigate(`${basePath}/submits`);
        break;
    }
  };

  return (
    <>
      <PageHeader
        loading={loading}
        avatar={<NoteAltIcon color="primary" />}
        title={item.name}
        sx={{ mt: -3 }}
      >
        <Box sx={{ position: "relative" }}>
          <Switch
            color="success"
            checked={item.status === WaiverStatus.ACTIVE}
            onChange={e =>
              handleStatusChange(
                e.target.checked ? WaiverStatus.ACTIVE : WaiverStatus.INACTIVE
              )
            }
          />
          <Typography
            variant="caption"
            sx={{ position: "absolute", top: -8, right: 10, zIndex: 100 }}
          >
            {item.status === WaiverStatus.ACTIVE
              ? I18n.t("Waiver.status.active")
              : I18n.t("Waiver.status.inactive")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          endIcon={sharings.length > 1 ? <ArrowDropDownIcon /> : undefined}
          onClick={handlePreviewClick}
          disabled={sharings.length === 0}
        >
          Open
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {sharings.map(sharing => (
            <MenuItem
              key={sharing.id}
              onClick={() => handleSharingSelect(sharing)}
            >
              {sharing.name}
            </MenuItem>
          ))}
        </Menu>
        <FormEditorMenu waiverId={item.id} />
      </PageHeader>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            minHeight: 0,
            "& .MuiButton": {
              minHeight: "48px",
            },
          }}
        >
          <Tab
            icon={<VisibilityIcon />}
            label={I18n.t("Waiver.preview")}
            iconPosition="start"
          />

          <Tab
            icon={
              <Box sx={{ position: "relative" }}>
                <Badge
                  badgeContent={isDirty ? 1 : 0}
                  variant="dot"
                  color="warning"
                  sx={{ position: "absolute", right: -5, top: 0 }}
                ></Badge>
                <EditIcon />
              </Box>
            }
            label={I18n.t("Waiver.edit")}
            iconPosition="start"
          />
          <Tab
            icon={<ShareIcon />}
            label={I18n.t("Waiver.sharing")}
            iconPosition="start"
            disabled={isDirty}
          />
          <Tab
            icon={<AssignmentTurnedIn />}
            label={I18n.t("Waiver.submits")}
            iconPosition="start"
            disabled={isDirty}
          />
        </Tabs>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Outlet context={waiverContext} />
      </Box>
      <Dialog {...sharingDialog} maxWidth="sm">
        <SharingForm
          id="create"
          defaultValues={{ waiver_ids: [waiver_id as any] }}
          afterSave={newItem => {
            if (sharingDialog?.onClose) {
              sharingDialog.onClose({}, "backdropClick");
            }
            navigate(`/waivers/${waiver_id}/publish/${newItem.id}`);
          }}
          onClose={sharingDialog.onClose}
        />
      </Dialog>
    </>
  );
};

export default function WaiverTabs() {
  const { waiver_id } = useParams();

  return (
    <WaiverProvider id={waiver_id}>
      <TabContent />
    </WaiverProvider>
  );
}
